/**
 * What people did with Leo's category suggestions, kept small.
 *
 * Add and Skip nudge the next suggestion of the same category: one that is
 * skipped over and over drifts down, one that is accepted over and over drifts
 * up. Nothing else is learned and nothing leaves the browser. It is a light
 * correction on top of reading the stem, not a model.
 */

import { createPersistedStore, usePersistedStore } from "@/lib/persisted-store"

export type SuggestionFeedback = {
  added: Record<string, number>
  skipped: Record<string, number>
}

const store = createPersistedStore<SuggestionFeedback>("shell:category-suggestion-feedback:v1", () => ({
  added: {},
  skipped: {},
}))

export function recordSuggestionFeedback(categoryId: string, outcome: "added" | "skipped"): void {
  store.set((current) => {
    const key = outcome
    return { ...current, [key]: { ...current[key], [categoryId]: (current[key][categoryId] ?? 0) + 1 } }
  })
}

/** Skipped twice or more drops it; added twice or more lifts it. Never out of 0.3 to 0.97. */
export function adjustConfidence(confidence: number, categoryId: string, feedback: SuggestionFeedback): number {
  const skipped = feedback.skipped[categoryId] ?? 0
  const added = feedback.added[categoryId] ?? 0
  let next = confidence
  if (skipped >= 2) next -= 0.15
  if (added >= 2) next += 0.05
  return Math.min(0.97, Math.max(0.3, next))
}

export function useSuggestionFeedback(): SuggestionFeedback {
  return usePersistedStore(store)[0]
}
